/**
 * "Do the same to the other estimates?"
 *
 * Raised once when a section is left, never per line. Jonathon, 2026-08-22, on
 * whether it should ask each time: "one prompt covering everything you changed
 * while you were in that tab" — so this lists what happened, lists who it could
 * happen to, and applies the ticked ones in one go.
 *
 * What it will not do quietly: change a quantity the two estimates already
 * disagreed about (a separate tick, off by default) or overwrite a price they
 * already disagreed about (never — it says so and moves on).
 */

import {
  actionIsLive,
  describeChange,
  MIRROR_BUCKET_LABEL,
  type MirrorBucket,
  type MirrorChange,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t, tn } from "../../i18n";
import { askKey, type MirrorPlan } from "./mirror";

export function MirrorPrompt({
  bucket,
  sourceLabel,
  changes,
  plans,
  busy,
  error,
  onClose,
  onApply,
}: {
  bucket: MirrorBucket;
  /** The estimate the changes were made on, e.g. "Option A". */
  sourceLabel: string;
  changes: readonly MirrorChange[];
  plans: readonly MirrorPlan[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onApply: (selection: {
    estimateIds: ReadonlySet<string>;
    asks: ReadonlySet<string>;
  }) => void;
}) {
  const [chosen, setChosen] = useState<Set<string>>(
    () =>
      new Set(
        plans
          .filter((p) => !p.blocked && p.candidate.defaultOn)
          .map((p) => p.candidate.estimate.id),
      ),
  );
  const [asks, setAsks] = useState<Set<string>>(new Set());

  const toggle = (set: Set<string>, key: string): Set<string> => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };

  const nothingChosen = chosen.size === 0;

  return (
    <Modal
      title={t("Apply these changes to the other estimates?")}
      subtitle={t("You changed the {bucket} on {source}.", {
        bucket: t(MIRROR_BUCKET_LABEL[bucket]),
        source: sourceLabel,
      })}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose} disabled={busy}>
            {t("No, just this one")}
          </button>
          <button
            className="btn-primary"
            disabled={busy || nothingChosen}
            onClick={() => onApply({ estimateIds: chosen, asks })}
          >
            {busy ? t("Applying…") : t("Apply to selected")}
          </button>
        </>
      }
    >
      {error && <ErrorNote error={error} />}

      <div className="chk-sub">{t("What changed")}</div>
      <ul className="mirror-changes">
        {changes.map((change, i) => (
          <li key={i}>{describeChange(change)}</li>
        ))}
      </ul>

      <div className="chk-sub">{t("Also apply to")}</div>
      {plans.length === 0 && (
        <div className="na-hint">
          {t("There are no other estimates on this job to apply them to.")}
        </div>
      )}

      {plans.map((plan) => {
        const id = plan.candidate.estimate.id;
        const live = plan.actions.filter(actionIsLive);
        const skips = plan.actions.flatMap((a) => a.skipped);
        const questions = plan.actions.flatMap((a, index) =>
          a.ask.map((ask) => ({ ask, index })),
        );

        return (
          <div className="mirror-target" key={id}>
            <label className="mirror-pick">
              <input
                type="checkbox"
                checked={chosen.has(id)}
                disabled={Boolean(plan.blocked) || busy}
                onChange={() => setChosen((s) => toggle(s, id))}
              />
              <b>{plan.candidate.label}</b>{" "}
              {plan.blocked ? (
                <span className="na-hint">
                  {t("— cannot be changed ({why})", { why: t(plan.blocked) })}
                </span>
              ) : live.length === 0 ? (
                <span className="na-hint">{t("— already matches")}</span>
              ) : (
                <span className="na-hint">
                  {tn(live.length, "— 1 change", "— {n} changes")}
                </span>
              )}
            </label>

            {/* The quantity questions. Off by default: two estimates measured
                differently are different on purpose, and the roof does not
                change size because a line was edited. */}
            {chosen.has(id) &&
              questions.map(({ ask, index }) => {
                const key = askKey(id, index);
                const change = plan.actions[index]!.change;
                const name =
                  change.kind === "changed"
                    ? change.before.label || change.after.label
                    : change.row.label;
                return (
                  <label className="mirror-ask" key={key}>
                    <input
                      type="checkbox"
                      checked={asks.has(key)}
                      disabled={busy}
                      onChange={() => setAsks((s) => toggle(s, key))}
                    />
                    {t(
                      "{name} — {label} has {theirs}, change it to {mine} as well?",
                      {
                        name: name || t("That line"),
                        label: plan.candidate.label,
                        theirs: ask.theirs,
                        mine: ask.mine,
                      },
                    )}
                  </label>
                );
              })}

            {/* Rule four: skipped, and told. */}
            {chosen.has(id) &&
              skips.map((note, i) => (
                <div className="na-hint mirror-skip" key={i}>
                  {note}
                </div>
              ))}
          </div>
        );
      })}
    </Modal>
  );
}
